import React, { useState, useEffect } from 'react';
import Ship from './Ship';
import Chart from 'chart.js/auto';
import './ShipDashboard.css';
import ChartAnnotation from 'chartjs-plugin-annotation';
import 'chartjs-plugin-annotation';

function ShipDashboard() {
  const [ships, setShips] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('All');
  const [selectedCustomer, setSelectedCustomer] = useState('All');
  const [customerData, setCustomerData] = useState([]);
  const [selectedType, setSelectedType] = useState('All');
  const [chartInstance, setChartInstance] = useState(null);
  const [thirdChartInstance, setThirdChartInstance] = useState(null);
  const [expandedShips, setExpandedShips] = useState([]);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    fetch('https://backend-nu-two.vercel.app/getAllShips')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        const sortedShips = data.sort((a, b) => a.SHIPNAME.localeCompare(b.SHIPNAME));
        setShips(sortedShips);
  
        // Standardize "prop" variations by trimming leading/trailing spaces
        const standardizeProductName = (product) => {
          const trimmedProduct = product.trim();
          if (trimmedProduct === "prop" || trimmedProduct === "prop ") {
            return "prop";
          }
          return product;
        };
  
        // Extract unique products, customers, and types from the ship data
        const uniqueProducts = ['All', ...new Set(sortedShips.map(ship => standardizeProductName(ship.Product)).filter(product => product !== ''))];
        const uniqueCustomers = ['All', ...new Set(sortedShips.map(ship => ship['Customer Name'].trim()).filter(name => name !== ''))].sort();
        const uniqueTypes = ['All', ...new Set(sortedShips.map(ship => ship.TYPE_NAME.trim()))];
        console.log('Products from API:', uniqueProducts);
  
        setProducts(uniqueProducts);
        setCustomers(uniqueCustomers);
        setTypes(uniqueTypes);
      })
      .catch(error => console.error('Error fetching ships:', error));
  }, []);
  
  
  
  const debug = true; // Set to true to enable debug logging, false to disable

  const filteredShips = ships.filter(ship =>
    (selectedProduct === 'All' || ship.Product === selectedProduct) &&
    (selectedCustomer === 'All' || ship['Customer Name'] === selectedCustomer) &&
    (selectedType === 'All' || ship.TYPE_NAME === selectedType)
  );

  useEffect(() => {
    if (debug) {
      console.log('useEffect triggered for selectedCustomer:', selectedCustomer);
    }
  
    if (selectedCustomer !== 'All') {
      const filteredData = ships.filter(ship => ship['Customer Name'] === selectedCustomer);
      if (debug) {
        console.log('Filtered data:', filteredData);
      }
      setCustomerData(filteredData);
    } else {
      if (debug) {
        console.log('Setting customerData with all ships.');
      }
      setCustomerData(ships); // Set customerData to all ships when 'All' is selected
    }
  }, [selectedCustomer, ships]);
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'long' }); // Get the month name
    const year = date.getFullYear(); // Get the year
    return `${month} ${year}`; // Return the formatted string (e.g., "February 2024")
  };
  
   
  const getWeekRange = (date) => {
    let start = new Date(date.getFullYear(), 0, 1); // Start of the year
    let end = new Date(date.getFullYear() + 1, 0, 1); // Start of the next year
    let days = Math.floor((date - start) / (24 * 60 * 60 * 1000)) + ((start.getDay() + 6) % 7);
    let weekNumber = Math.ceil(days / 7);
    let weekStart = new Date(start.valueOf()); // Copy start date
    weekStart.setDate(weekStart.getDate() + ((weekNumber - 1) * 7)); // Go to the first day of the week
    weekStart.setDate(weekStart.getDate() - (weekStart.getDay() ? weekStart.getDay() - 1 : 6)); // Adjust to Monday
    let weekEnd = new Date(weekStart.valueOf());
    weekEnd.setDate(weekEnd.getDate() + 6); // End of the week (Sunday)
  
    // Debug statements
    //console.log('Start of week:', weekStart);
    //console.log('End of week:', weekEnd);
  
    // Format the start and end dates
    let formattedStartDate = `${weekStart.getMonth() + 1}/${weekStart.getDate()}/${weekStart.getFullYear()}`;
    let formattedEndDate = `${weekEnd.getMonth() + 1}/${weekEnd.getDate()}/${weekEnd.getFullYear()}`;
    
    // Include the formatted dates in the label
    let label = `${formattedStartDate} - ${formattedEndDate}`;
    return label;
  };
  
  const getWeekLabel = (date) => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
  const numberOfDays = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((numberOfDays + startOfYear.getDay() + 1) / 7);
  return `Week ${weekNumber}`;
};

const initializeVesselCounts = (startDate, endDate) => {
  let currentDate = new Date(startDate);
  const vesselCountsByWeek = {};
  while (currentDate <= endDate) {
    const weekRange = getWeekRange(new Date(currentDate));
    vesselCountsByWeek[weekRange] = 0; // Initialize counts to 0
    currentDate.setDate(currentDate.getDate() + 7); // Move to the next week
  }
  return vesselCountsByWeek;
};

const renderThirdChart = () => {
  Chart.register(ChartAnnotation);
  if (thirdChartInstance) {
    thirdChartInstance.destroy();
  }

  const startDate = new Date('2022-01-01');
  const endDate = new Date();
  const vesselCountsByWeek = initializeVesselCounts(startDate, endDate);
  let totalVesselCount = 0;

  const filteredShips = ships.filter(ship => 
    (selectedProduct === 'All' || ship.Product === selectedProduct) &&
    (selectedCustomer === 'All' || ship['Customer Name'] === selectedCustomer)
  )

  filteredShips.forEach(ship => {
    const ddDate = new Date(ship['DD Date']);
    if (isNaN(ddDate.getTime())) {
      return;
    }
    const monthYear = formatDate(ddDate);
    const weekLabel = getWeekRange(ddDate);
    if (weekLabel) {
      vesselCountsByWeek[weekLabel] = (vesselCountsByWeek[weekLabel] || 0) + 1;
      totalVesselCount++;
    }
  });

// Aggregate counts by month for displaying on the x-axis
const vesselCountsByMonthAndProduct = {};
  filteredShips.forEach(ship => {
    const ddDate = new Date(ship['DD Date']);
    if (isNaN(ddDate.getTime())) {
      return;
    }
    const monthYear = formatDate(ddDate);
    const product = ship.Product;
    if (!vesselCountsByMonthAndProduct[monthYear]) {
      vesselCountsByMonthAndProduct[monthYear] = {};
    }
    if (!vesselCountsByMonthAndProduct[monthYear][product]) {
      vesselCountsByMonthAndProduct[monthYear][product] = 0;
    }
    vesselCountsByMonthAndProduct[monthYear][product]++;
  });

  // Prepare chart labels (months)
  const chartLabels = Object.keys(vesselCountsByMonthAndProduct).sort((a, b) => {
    const [aMonth, aYear] = a.split(' ');
    const [bMonth, bYear] = b.split(' ');
    // Create date objects for comparison
    return new Date(`${aMonth} 1, ${aYear}`) - new Date(`${bMonth} 1, ${bYear}`);
  });
  
//const chartData = chartLabels.map(label => vesselCountsByMonth[label]);
const datasets = [];
products.forEach(product => {
  const productColor = getColorForProduct(product);
  const data = chartLabels.map(monthYear => vesselCountsByMonthAndProduct[monthYear] && vesselCountsByMonthAndProduct[monthYear][product] ? vesselCountsByMonthAndProduct[monthYear][product] : 0);
  datasets.push({
    label: product,
    data: data,
    backgroundColor: productColor,
    stack: 'Stack 0',
  });
});

const quarterLines = [];
for (let year = startDate.getFullYear(); year <= endDate.getFullYear(); year++) {
  for (let quarter = 1; quarter <= 4; quarter++) {
    const quarterDate = new Date(year, (quarter - 1) * 3, 1);
    if (quarterDate >= startDate && quarterDate <= endDate) {
      quarterLines.push({
        mode: 'vertical',
        scaleID: 'x',
        value: quarterDate.getTime(),
        borderColor: 'rgba(0, 0, 0, 0.65)',
        borderWidth: 2,
        borderDash: [5, 5],
        label: {
          enabled: true,
          content: `Q${quarter} ${year}`,
          position: 'top',
          color: darkMode ? '#fff' : '#000' // Adjust text color for the quarter labels
        }
      });
    }
  }
}

const ctx = document.getElementById('thirdChart').getContext('2d');
const chart = new Chart(ctx, {
  type: 'bar',
  data: {
    labels: chartLabels,
    datasets: datasets
  },
  options: {
    scales: {
      x: {
        id: 'x-axis-0',
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
          color: darkMode ? '#fff' : '#000' // Adjust tick color for x-axis
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: darkMode ? '#fff' : '#000' // Adjust tick color for y-axis
        }
      }
    },
    plugins: {
      annotation: {
        annotations: quarterLines
      },
      legend: {
        labels: {
          color: darkMode ? '#fff' : '#000' // Adjust legend text color
        }
      }
    }
  }
});

setThirdChartInstance(chart);
};

useEffect(() => {
  // Call renderThirdChart whenever ships, selectedProduct, or selectedCustomer changes
  if (ships.length > 0 && (selectedProduct === 'All' || products.includes(selectedProduct)) && (selectedCustomer === 'All' || customers.includes(selectedCustomer))) {
    renderThirdChart();
  }
}, [ships, selectedProduct, selectedCustomer, products,darkMode]);

  // Function to generate the date range with weekly intervals
  const generateDateRange = (startDate, endDate) => {
    const dateRange = {};
    let currentDate = new Date(startDate);
    let weekNumber = 1;
    while (currentDate <= endDate) {
        const weekStart = new Date(currentDate);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6); // Assuming a week ends on the 7th day
        dateRange[`W${weekNumber}`] = `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
        currentDate.setDate(currentDate.getDate() + 7);
        weekNumber++;
    }
    return dateRange;
};

  // Function to count the number of vessels for each week within the date range
  const countVesselsByWeek = (dateRange) => {
    const vesselCounts = { ...dateRange };
    ships.forEach(ship => {
      const ddDate = new Date(ship['DD Date']);
      Object.keys(vesselCounts).forEach(week => {
        const [start, end] = week.split(' - ');
        const weekStart = new Date(start);
        const weekEnd = new Date(end);
        if (ddDate >= weekStart && ddDate < weekEnd) {
          vesselCounts[week]++;
        }
      });
    });
    return vesselCounts;
  };
  const handleProductChange = (event) => {
    setSelectedProduct(event.target.value);
    if (thirdChartInstance) {
      thirdChartInstance.destroy(); // Destroy the existing third chart instance
    }
    if (event.target.value !== 'All') {
      renderThirdChart(); // Render the third chart for the selected product
    }
  };

  const handleCustomerChange = (event) => {
    const selectedCustomer = event.target.value;
    setSelectedCustomer(selectedCustomer);
  
    // Filter ships based on the selected customer
    const filteredData = selectedCustomer === 'All' ? ships : ships.filter(ship => ship['Customer Name'] === selectedCustomer);
    setCustomerData(filteredData);
    
    if (chartInstance) {
      chartInstance.destroy(); // Destroy the existing chart instance
    }
    if (selectedCustomer !== 'All') {
      renderChart(selectedCustomer); // Render the pie chart for the selected customer
    }
    renderVesselList(selectedCustomer);
  };
  
  
// Function to assign color based on product
const getColorForProduct = (product) => {
  // Define colors for each product manually
  const productColors = {
    'prop': 'rgba(255, 99, 132, 0.7)', // Pink
    'fuel white': 'rgba(128, 128, 0, 0.7)',
    'fuel red': "#dc3545", // Red
    'fuel black': '#585858',// Dark Gray
    'fuel red, prop':  'rgba(0, 255, 0, 0.7)',
    'fuel red, urn': 'rgba(255, 206, 86, 0.7)', // Yellow 
    'shield, fuel black': 'rgba(75, 192, 192, 0.7)',   
    'prop, fuel red test patch':'rgba(128,128,128,0.8)',
    'fuel blue & prop': "#20c997", //Blue
    'fuel white, prop': "#17a2b8", //Cyan
  };

  // Return the color for the specified product, or a default color if not found
  return productColors[product.toLowerCase()] || 'rgba(153, 102, 255, 0.7)'; // Default: Purple
};

const toggleExpansion = (shipId) => {
  setExpandedShips(prevState => {
    if (prevState.includes(shipId)) {
      return prevState.filter(id => id !== shipId); // Remove shipId from expandedShips
    } else {
      return [...prevState, shipId]; // Add shipId to expandedShips
    }
  });
};

const renderVesselList = () => {
  // Filter ships based on the selected customer
  const filteredShips = selectedCustomer === 'All' ? ships : ships.filter(ship => ship['Customer Name'] === selectedCustomer);

  // Function to toggle expansion state for a vessel
 
  

  return (
    
    <ul className="vessel-list">
      {filteredShips.map(ship => (
        <li key={ship.SHIP_ID} className="vessel-list-item" onClick={() => toggleExpansion(ship.SHIP_ID)}>
          <div className="vessel-name">
            {ship.SHIPNAME}
            {expandedShips.includes(ship.SHIP_ID) && (
              <div className="expanded-info">
                <span className="product-info">Product: {ship.Product}</span>
                <span className="additional-info">Additional Info: Add your additional information here</span>
              </div>
            )}
          </div>
          <span className="dd-date">DD DDate: {ship['DD Date']}</span>
        </li>
      ))}
    </ul>
  );
};

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const renderChart = (selectedCustomer) => {
    const customerData = ships.filter(ship => ship['Customer Name'] === selectedCustomer);
    const productCounts = {};
    customerData.forEach(ship => {
      productCounts[ship.Product] = (productCounts[ship.Product] || 0) + 1;
    });
    const chartLabels = Object.keys(productCounts);
    const chartData = Object.values(productCounts);

    const ctx = document.getElementById('productChart').getContext('2d');
    const chart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: chartLabels,
        datasets: [{
          label: 'Product Distribution',
          data: chartData,
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 206, 86, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)',
            'rgba(255, 159, 64, 0.7)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false, // Ensure the chart maintains its aspect ratio
        plugins: {
          legend: {
            labels: {
              color: darkMode ? '#fff' : '#000' // Adjust legend text color
            }
          }
        }
      }
    });
    setChartInstance(chart); // Store the pie chart instance in state
  };

  return (
    <div className={darkMode ? 'dark-mode' : ''}>
     <button className={`toggle-button ${darkMode ? 'dark-mode' : ''}`} onClick={() => setDarkMode(!darkMode)}>
        <img src={require('./dark.png')} alt="Dark Mode" style={{ width: '25px', height: '25px' }} />
      </button>
      
      {/* Filter Container */}
      <div className="filter-container">
        {/* Dropdown for Product */}
        <div className="dropdown-container">
          <label htmlFor="product-filter" className="dropdown-label">Product Applied:</label>
          <select
            id="product-filter"
            className="dropdown"
            value={selectedProduct}
            onChange={handleProductChange}
          >
            {products.map(product => (
              <option key={product} value={product}>{product}</option>
            ))}
          </select>
        </div>
  
        {/* Dropdown for Customer */}
        <div className="dropdown-container">
          <label htmlFor="customer-filter" className="dropdown-label">Customer Name:</label>
          <select
            id="customer-filter"
            className="dropdown"
            value={selectedCustomer}
            onChange={handleCustomerChange}
          >
            {customers.map(customer => (
              <option key={customer} value={customer}>{customer}</option>
            ))}
          </select>
        </div>
  
        {/* Dropdown for Type */}
        <div className="dropdown-container">
          <label htmlFor="type-filter" className="dropdown-label">Type:</label>
          <select
            id="type-filter"
            className="dropdown"
            value={selectedType}
            onChange={handleTypeChange}
          >
            {types.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>
  
      {/* Charts are only displayed when a customer is selected */}
      {selectedCustomer !== 'All' && (
        <div className="chart-wrapper">
          {/* Pie Chart Container */}
          <div className="chart-container">
            <canvas id="productChart"></canvas>
          </div>
  
          <ul className="vessel-list">
      {filteredShips.map(ship => (
        <li key={ship.SHIP_ID} className="vessel-list-item" onClick={() => toggleExpansion(ship.SHIP_ID)}>
          <div className="vessel-name">
            {ship.SHIPNAME}
            {expandedShips.includes(ship.SHIP_ID) && (
              <div className="expanded-info">
                <span className="product-info">Product Applied: {ship.Product}</span>
                <span className="additional-info">Additional Info: Added in the future</span>
              </div>
            )}
          </div>
          <span className="dd-date">DD DDate: {ship['DD Date']}</span>
        </li>
      ))}
    </ul>
        </div>
      )}
  
      {/* Third Chart Container - Always displayed below */}
      <div id="thirdChart-container" className="chart-box-shadow">
        <canvas id="thirdChart"></canvas>
      </div>
  
      {/* Ship Cards Container */}
      <div className="ship-card-container">
        {ships
          .filter(ship =>
            (selectedProduct === 'All' || ship.Product === selectedProduct) &&
            (selectedCustomer === 'All' || ship['Customer Name'] === selectedCustomer) &&
            (selectedType === 'All' || ship.TYPE_NAME === selectedType)
          )
          .map((ship, index) => (
            <div className="ship-card" key={index}>
              <Ship
                name={ship.SHIPNAME}
                temperature={ship.TEMPERATURE}
                salinity={ship.SALINITY}
                speed={ship.SPEED}
                lastPort={ship.LAST_PORT}
                type={ship.TYPE_NAME}
                avg_speed={ship.AVG_SPEED}
                max_speed={ship.MAX_SPEED}
                distance={ship.DISTANCE_TRAVELLED}
                product={ship.Product}
                customerName={ship['Customer Name']}
                typeName={ship.TYPE_NAME}
              />
            </div>
          ))}
      </div>
    </div>
  ); }

export default ShipDashboard;
